import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useTranslation } from 'react-i18next';
import WarningModalContent from 'Components/ConcurrencyHelpers/WarningModalContent';
import { UseMutationOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getTranslationErrorHandling } from '../helper';

type MutationOptions = UseMutationOptions<AxiosResponse<any>, unknown, any, unknown>;

type Args = {
    mode: 'overview' | 'rule' | 'deleteMerchant' | 'deleteMerchantRule';
    queryInvalidateFunction: () => void;
    // required for rule type
    ruleId?: string;
    mutationOptions: MutationOptions;
    mid?: string;
};

/**
 * Common error handler hook for all queries related to
 * updated an individual merchant's data.
 */

function useSingleMerchantErrorHandlerHandler(
    { mode, mutationOptions, ruleId, queryInvalidateFunction, mid }: Args = {
        mode: 'deleteMerchant',
        mutationOptions: {},
        queryInvalidateFunction: () => {}
    }
) {
    const { t } = useTranslation();
    const singleMerchantStringKey = getTranslationErrorHandling(t, 'singleMerchant');
    const errorStringKey = getTranslationErrorHandling(t);

    const history = useHistory();
    const { addTechnicalErrorModal, addModal } = useReduxModal();
    const { ruleId: currentRuleId, mid: merchantId } = useParams<{
        ruleId: string;
        mid: string;
    }>();
    const { onError } = mutationOptions;
    const isOverview = mode === 'overview';
    const isDeleteMerchantRule = mode === 'deleteMerchantRule';

    const modalTitle = singleMerchantStringKey(isOverview ? 'overviewTitle' : 'ruleTitle');

    const errorHandler = React.useCallback(
        (error: any, variables?: any, context?: any) => {
            const handleNotFound = () => {
                // if we're deleting a merchant rule can get a 404 if the rule was deleted. Just invalidate the query
                // if someone tries to delete a rule from a deleted merchant
                // they'll be shown the 404 page but that's okay.
                if (isDeleteMerchantRule) {
                    queryInvalidateFunction();
                }

                if (mid) {
                    addTechnicalErrorModal(
                        '',
                        singleMerchantStringKey('Merchant Override Already Removed'),
                        () => {
                            history.push(`/overrides/merchant/${mid}`);
                        },
                        {
                            status: 'warning',
                            content: singleMerchantStringKey('merchantOverrideRemoved')
                        }
                    );
                } else {
                    addTechnicalErrorModal(
                        '',
                        singleMerchantStringKey('Merchant Deleted'),
                        () => {
                            history.push('/overrides');
                        },
                        {
                            status: 'warning',
                            content: singleMerchantStringKey('merchantDeleted')
                        }
                    );
                }
            };

            const handleConflict = () => {
                addTechnicalErrorModal('', modalTitle, queryInvalidateFunction, {
                    status: 'warning',
                    content: (
                        <WarningModalContent
                            title={
                                isOverview
                                    ? singleMerchantStringKey('overviewPreamble')
                                    : errorStringKey('rulePreamble', { ruleId })
                            }
                            subTitle={errorStringKey('retry')}
                        />
                    )
                });
            };

            const handleDefault = () => {
                const preContentMessage = isOverview
                    ? singleMerchantStringKey('genericMessage')
                    : errorStringKey('ruleFailed', { ruleId });
                addTechnicalErrorModal(preContentMessage, errorStringKey('unableToSave'));
            };

            const handleRuleNotFound = () => {
                if (error.response?.data?.message === `No rule template found with rule id: ${currentRuleId}`) {
                    addModal({
                        id: 'update-merchant-override-rule-error',
                        status: 'warning',
                        title: t('Error Saving Override'),
                        content: t('Override Could Not Be Saved'),
                        showCloseIcon: false,
                        showCloseButton: false,
                        closeOnAction: true,
                        onActionText: 'Confirm',
                        onClose: () => {
                            history.replace(`/overrides/merchant/${merchantId}`);
                        }
                    });
                } else {
                    handleDefault();
                }
            };

            switch (error?.response?.status || error?.status) {
                case 400:
                    handleRuleNotFound();
                    break;
                case 404:
                    handleNotFound();
                    break;
                case 409:
                    handleConflict();
                    break;
                default:
                    handleDefault();
                    break;
            }

            onError?.(error, variables, context);
        },
        [
            onError,
            isDeleteMerchantRule,
            mid,
            queryInvalidateFunction,
            addTechnicalErrorModal,
            singleMerchantStringKey,
            history,
            modalTitle,
            isOverview,
            errorStringKey,
            ruleId,
            t,
            addModal,
            currentRuleId,
            merchantId
        ]
    );

    return errorHandler;
}

export default useSingleMerchantErrorHandlerHandler;
